import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Combobox,
  Option,
  Skeleton,
  SkeletonItem,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import { useContainers } from '@/hooks/useContainers';
import { useFolders } from '@/hooks/useFolders';
import { buildFolderTree, type FolderNode } from './buildTree';
import { useCopyMail, useMoveMail } from '@/hooks/useMailMutations';
import { useRecentProjectsStore } from '@/stores/recentProjectsStore';
import { filterListableContainers } from '@/utils/listableContainer';
import type { Container } from '@/types/domain';

const useStyles = makeStyles({
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  label: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase200 },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
});

function flatten(
  nodes: FolderNode[],
  depth = 0,
): Array<{ id: string; label: string; name: string }> {
  const out: Array<{ id: string; label: string; name: string }> = [];
  for (const n of nodes) {
    out.push({
      id: n.folder.id,
      label: `${'\u00A0\u00A0\u00A0'.repeat(depth)}${n.folder.name}`,
      name: n.folder.name,
    });
    out.push(...flatten(n.children, depth + 1));
  }
  return out;
}

/**
 * Ce que ce dialogue exige VRAIMENT d'un mail — pas un `MailSummary` complet.
 *
 * Un résultat de recherche n'est pas un `MailSummary` : `fn_search` renvoie l'id,
 * le conteneur, le dossier et un libellé déjà résolu, mais ni la taille, ni
 * l'expéditeur, ni les dates. Exiger le type complet obligeait à fabriquer un
 * faux objet (ou à recharger le mail) pour une action qui ne lit que ces cinq
 * champs. Un `MailSummary` satisfait cette forme : les appelants existants sont
 * inchangés.
 */
export interface MailTargetItem {
  id: string;
  /** `null` = rangé à la racine du projet. */
  folderId: string | null;
  containerId: string;
  displayName?: string | null;
  subject?: string | null;
}

interface Props {
  mode: 'copy' | 'move';
  mail: MailTargetItem;
  onClose: () => void;
}

// Pick a destination folder, then copy or move the mail there. Mounted only
// while open (caller conditionally renders) so state resets each time.
export function MailTargetDialog({ mode, mail, onClose }: Props) {
  const styles = useStyles();
  const { data: containers } = useContainers();
  const recent = useRecentProjectsStore((s) => s.recent);

  const [targetContainerId, setTargetContainerId] = useState<string>(mail.containerId);
  const [targetContainerQuery, setTargetContainerQuery] = useState<string>('');
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
  const [targetFolderQuery, setTargetFolderQuery] = useState<string>('');

  const { data: targetFolders, isPending: foldersLoading } = useFolders(targetContainerId);
  const folderOptions = useMemo(
    () => (targetFolders ? flatten(buildFolderTree(targetFolders)) : []),
    [targetFolders],
  );
  // Keyword filter over the destination folders (matches the folder name, not
  // its indentation prefix).
  const filteredFolderOptions = useMemo(() => {
    const q = targetFolderQuery.trim().toLowerCase();
    if (!q) return folderOptions;
    return folderOptions.filter((f) => f.name.toLowerCase().includes(q));
  }, [folderOptions, targetFolderQuery]);

  // 0029 : un espace sans groupe Microsoft 365 ne peut pas être une destination —
  // le serveur refuserait la copie en 409.
  const all = filterListableContainers((containers ?? []) as Container[]) as Container[];
  const byId = new Map(all.map((c) => [c.id, c]));
  const recentSet = new Set(recent.map((r) => r.id));
  const sortedContainers = useMemo(() => {
    const recentOnes = recent
      .map((r) => byId.get(r.id))
      .filter((c): c is Container => Boolean(c));
    const others = all
      .filter((c) => !recentSet.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
    return [...recentOnes, ...others];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, recent]);
  const filteredContainers = useMemo(() => {
    const q = targetContainerQuery.trim().toLowerCase();
    if (!q) return sortedContainers;
    return sortedContainers.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.number ? c.number.toLowerCase().includes(q) : false),
    );
  }, [sortedContainers, targetContainerQuery]);

  const targetContainer = byId.get(targetContainerId) ?? null;
  const targetFolderLabel = folderOptions.find((f) => f.id === targetFolderId)?.label ?? '';

  const copy = useCopyMail();
  const move = useMoveMail();
  const busy = copy.isPending || move.isPending;
  const err = copy.error ?? move.error;

  const submit = () => {
    if (!targetFolderId) return;
    const vars = { mailId: mail.id, sourceFolderId: mail.folderId, targetFolderId };
    const opts = { onSuccess: () => onClose() };
    if (mode === 'copy') copy.mutate(vars, opts);
    else move.mutate(vars, opts);
  };

  const verb = mode === 'copy' ? 'Copier' : 'Déplacer';
  const title = mail.displayName || mail.subject || '(sans objet)';

  return (
    <Dialog open onOpenChange={(_e, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {verb} « {title} »
          </DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className={styles.group}>
                <Text className={styles.label}>Projet ou espace de destination</Text>
                <Combobox
                  freeform
                  placeholder="Taper pour filtrer…"
                  value={targetContainerQuery || targetContainer?.name || ''}
                  selectedOptions={[targetContainerId]}
                  onInput={(e) => setTargetContainerQuery((e.target as HTMLInputElement).value)}
                  onOptionSelect={(_e, data) => {
                    if (data.optionValue) {
                      setTargetContainerId(data.optionValue);
                      setTargetFolderId(null);
                      setTargetFolderQuery('');
                      setTargetContainerQuery(byId.get(data.optionValue)?.name ?? '');
                    }
                  }}
                >
                  {filteredContainers.slice(0, 50).map((c) => (
                    <Option key={c.id} value={c.id} text={c.name}>
                      {c.name}
                    </Option>
                  ))}
                </Combobox>
              </div>

              <div className={styles.group}>
                <Text className={styles.label}>Dossier de destination</Text>
                {foldersLoading ? (
                  <Skeleton>
                    <SkeletonItem size={32} />
                  </Skeleton>
                ) : (
                  <Combobox
                    freeform
                    placeholder="Taper pour filtrer les dossiers…"
                    value={targetFolderQuery || targetFolderLabel}
                    selectedOptions={targetFolderId ? [targetFolderId] : []}
                    onInput={(e) => setTargetFolderQuery((e.target as HTMLInputElement).value)}
                    onOptionSelect={(_e, data) => {
                      setTargetFolderId(data.optionValue ?? null);
                      // Show the picked folder's label; clear the typed filter.
                      setTargetFolderQuery('');
                    }}
                  >
                    {filteredFolderOptions.length === 0 ? (
                      <Option key="__none" value="" text="" disabled>
                        Aucun dossier
                      </Option>
                    ) : (
                      filteredFolderOptions.map((f) => (
                        <Option key={f.id} value={f.id} text={f.label}>
                          {f.label}
                        </Option>
                      ))
                    )}
                  </Combobox>
                )}
              </div>

              {err && (
                <Text className={styles.hint} style={{ color: tokens.colorPaletteRedForeground1 }}>
                  {err.message}
                </Text>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Annuler
            </Button>
            <Button appearance="primary" disabled={busy || !targetFolderId} onClick={submit}>
              {busy ? `${verb}…` : verb}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
